import { useCallback, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import type { JobPostDetail } from "@/types/job_search/detail";

export type JobPostDetailStatus = "idle" | "loading" | "ready" | "error";

export const useJobPostDetail = (initialDetail?: JobPostDetail) => {
  const [detail, setDetail] = useState<JobPostDetail | null>(initialDetail ?? null);
  const [status, setStatus] = useState<JobPostDetailStatus>(initialDetail ? "ready" : "idle");
  const requestIdRef = useRef(0);

  const select = useCallback((url: string) => {
    const requestId = ++requestIdRef.current;
    setDetail(null);
    setStatus("loading");

    fetch(url, {
      headers: { "X-Modal-Request": "true" },
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: JobPostDetail) => {
        if (requestId !== requestIdRef.current) return;
        setDetail(data);
        setStatus("ready");
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setStatus("error");
        router.visit(url, { preserveScroll: true });
      });
  }, []);

  const clear = useCallback(() => {
    requestIdRef.current += 1;
    setDetail(null);
    setStatus("idle");
  }, []);

  return { detail, status, select, clear };
};

export default useJobPostDetail;
